import { useState, useEffect } from 'react';
import { Folder, Check, Plus, FolderPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface FolderType {
  id: string;
  name: string;
  color?: string;
}

interface FolderFilterDropdownProps {
  selectedFolders: string[];
  onFoldersChange: (folders: string[]) => void;
  organizationId: string;
  onFolderCreated?: () => void;
}

export function FolderFilterDropdown({
  selectedFolders,
  onFoldersChange,
  organizationId,
  onFolderCreated,
}: FolderFilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const [tempSelectedFolders, setTempSelectedFolders] = useState<string[]>(selectedFolders);
  const [folders, setFolders] = useState<FolderType[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);

  // Fetch folders based on search query
  useEffect(() => {
    const fetchFolders = async () => {
      if (!organizationId) return;

      let query = supabase
        .from('folders')
        .select('id, name, color')
        .eq('organization_id', organizationId)
        .order('name');

      if (searchQuery) {
        query = query.ilike('name', `%${searchQuery}%`);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching folders:', error);
        return;
      }

      setFolders(data || []);
    };

    const timeoutId = setTimeout(fetchFolders, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, organizationId]);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      // Reset temp selection and query when opening
      setTempSelectedFolders(selectedFolders);
      setSearchQuery('');
    }
  };

  const toggleFolder = (folderId: string) => {
    setTempSelectedFolders(prev =>
      prev.includes(folderId)
        ? prev.filter(id => id !== folderId)
        : [...prev, folderId]
    );
  };

  const applySelection = () => {
    onFoldersChange(tempSelectedFolders);
    setOpen(false);
  };

  const clearSelection = () => {
    setTempSelectedFolders([]);
  };

  const createNewFolder = async () => {
    if (!searchQuery.trim() || isCreatingFolder) return;

    setIsCreatingFolder(true);
    const newFolderName = searchQuery.trim();

    try {
      const { data, error } = await supabase
        .from('folders')
        .insert({
          name: newFolderName,
          organization_id: organizationId,
          color: `#${Math.floor(Math.random()*16777215).toString(16)}`,
          icon: 'folder',
        })
        .select()
        .single();

      if (error) throw error;

      setFolders(prev => [...prev, data]);
      setTempSelectedFolders(prev => [...prev, data.id]);
      setSearchQuery('');
      toast({ description: `Folder "${newFolderName}" created` });
      onFolderCreated?.();
    } catch (error) {
      console.error('Error creating folder:', error);
      toast({
        variant: 'destructive',
        description: 'Failed to create folder',
      });
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const selectedFolderNames = folders
    .filter(f => tempSelectedFolders.includes(f.id))
    .map(f => f.name);

  const showCreateOption = searchQuery.trim() && 
    !folders.some(f => f.name.toLowerCase() === searchQuery.toLowerCase());

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 relative">
          <Folder className="h-4 w-4" />
          {selectedFolders.length > 0 && (
            <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px]">
              {selectedFolders.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0 z-50 bg-popover" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search folders..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            <CommandEmpty>No folders found.</CommandEmpty>
            {selectedFolderNames.length > 0 && (
              <>
                <CommandGroup heading="Selected">
                  {folders
                    .filter(folder => tempSelectedFolders.includes(folder.id))
                    .map(folder => (
                      <CommandItem
                        key={folder.id}
                        onSelect={() => toggleFolder(folder.id)}
                        className="cursor-pointer"
                      >
                        <div className="flex items-center gap-2 flex-1">
                          {folder.color && (
                            <div
                              className="w-3 h-3 rounded-full shrink-0"
                              style={{ backgroundColor: folder.color }}
                            />
                          )}
                          <Folder className="h-4 w-4" />
                          <span>{folder.name}</span>
                        </div>
                        <Check className="h-4 w-4 ml-auto" />
                      </CommandItem>
                    ))}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup heading="Folders">
              {folders
                .filter(folder => !tempSelectedFolders.includes(folder.id))
                .map(folder => (
                  <CommandItem
                    key={folder.id}
                    onSelect={() => toggleFolder(folder.id)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      {folder.color && (
                        <div
                          className="w-3 h-3 rounded-full shrink-0"
                          style={{ backgroundColor: folder.color }}
                        />
                      )}
                      <Folder className="h-4 w-4" />
                      <span>{folder.name}</span>
                    </div>
                  </CommandItem>
                ))}
              {showCreateOption && (
                <>
                  <CommandSeparator />
                  <CommandItem
                    onSelect={createNewFolder}
                    className="cursor-pointer"
                    disabled={isCreatingFolder}
                  >
                    <FolderPlus className="h-4 w-4 mr-2" />
                    Create "{searchQuery}"
                  </CommandItem>
                </>
              )}
            </CommandGroup>
          </CommandList>
          <CommandSeparator />
          <div className="flex items-center gap-2 p-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSelection}
              disabled={tempSelectedFolders.length === 0}
              className="flex-1"
            >
              Clear
            </Button>
            <Button
              size="sm"
              onClick={applySelection}
              className="flex-1"
            >
              Apply
            </Button>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
